import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { platformAdminAPI } from '../services/api';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function loginMethodLabel(value) {
  const labels = {
    local: 'Standard platform login',
    microsoft: 'Microsoft Entra ID SSO',
    configure_later: 'Configure SSO later',
    google: 'Google Workspace SSO',
  };

  return labels[value] || 'Standard platform login';
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1 text-sm text-slate-950 ${mono ? 'font-mono break-all' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}

export default function PlatformAdminOnboardingRequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRequest = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await platformAdminAPI.getOnboardingRequest(requestId);
      setRequest(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load onboarding request.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const approve = async () => {
    const confirmed = window.confirm(
      'Approve this onboarding request? This will create the PII organization, tenant schema, domain, admin users, API key, default RBAC groups, and identity-provider foundation.'
    );

    if (!confirmed) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await platformAdminAPI.approveOnboardingRequest(requestId);
      setSuccess(res.data?.message || 'Request approved successfully.');
      await loadRequest();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to approve onboarding request.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required.');
      return;
    }

    const confirmed = window.confirm('Reject this onboarding request?');
    if (!confirmed) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await platformAdminAPI.rejectOnboardingRequest(
        requestId,
        rejectionReason.trim(),
      );
      setSuccess(res.data?.message || 'Request rejected successfully.');
      await loadRequest();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to reject onboarding request.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10 text-slate-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-orange-600" />
        Loading request...
      </div>
    );
  }

  if (!request) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">
        Request not found.
      </div>
    );
  }

  const canReview = request.status === 'pending_review';

  return (
    <div>
      <button
        onClick={() => navigate('/platform-admin/onboarding-requests')}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to requests
      </button>

      <div className="mb-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-gradient-to-r from-orange-50 via-white to-amber-50 p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-orange-600 p-3 text-white shadow-sm">
              <ShieldCheck className="h-6 w-6" />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-slate-950">
                {request.consent_org_name}
              </h1>
              <p className="mt-1 font-mono text-sm text-slate-600">
                {request.request_code}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Review Consent Platform verified organization before provisioning PII infrastructure.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-3">
          <InfoRow label="Status" value={request.status} />
          <InfoRow label="Submitted" value={formatDate(request.submitted_at)} />
          <InfoRow label="Reviewed" value={formatDate(request.reviewed_at)} />
          <InfoRow
            label="Login Method"
            value={loginMethodLabel(request.preferred_login_method)}
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          <CheckCircle2 className="mt-0.5 h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card title="Consent Platform Organization">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow label="Consent Org UUID" value={request.consent_org_uuid} mono />
              <InfoRow label="Org Slug" value={request.consent_org_slug} />
              <InfoRow label="Org Email" value={request.consent_org_email} />
              <InfoRow label="Created PII Org UUID" value={request.organization_uuid} mono />
            </div>
          </Card>

          <Card title="Requester">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow label="Name" value={request.requester_name} />
              <InfoRow label="Email" value={request.requester_email} />
              <InfoRow label="Phone" value={request.requester_phone} />
              <InfoRow label="Contact Email" value={request.contact_email} />
              <InfoRow label="Contact Phone" value={request.contact_phone} />
            </div>

            <div className="mt-4">
              <InfoRow label="Organization Address" value={request.organization_address} />
            </div>
          </Card>

          <Card title="Proposed PII Admins">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow label="Admin 1 Name" value={request.admin1_full_name} />
              <InfoRow label="Admin 1 Email" value={request.admin1_email} />
              <InfoRow label="Admin 2 Name" value={request.admin2_full_name} />
              <InfoRow label="Admin 2 Email" value={request.admin2_email} />
            </div>
          </Card>

                    <Card title="Identity & Login Setup">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow
                label="Preferred Login Method"
                value={loginMethodLabel(request.preferred_login_method)}
              />
              <InfoRow
                label="Identity Setup Status"
                value={request.identity_setup_status}
              />
              <InfoRow
                label="Microsoft Tenant Hint"
                value={request.microsoft_tenant_hint}
                mono
              />
            </div>

            {request.preferred_login_method === 'microsoft' && (
              <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 p-4">
                <p className="text-sm font-semibold text-slate-950">
                  Microsoft SSO selected
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Approval will provision the local workspace and create a pending
                  Microsoft identity-provider setup record. Microsoft admin consent,
                  assignment-required configuration, directory sync, and RBAC mapping
                  happen after approval.
                </p>
              </div>
            )}

            {request.preferred_login_method === 'configure_later' && (
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">
                  Configure later selected
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  The organization starts with standard platform login. Microsoft SSO
                  can be connected later from organization settings.
                </p>
              </div>
            )}
          </Card>

          <Card title="Business Context">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow label="Expected Volume" value={request.expected_volume} />
              <InfoRow
                label="Expected Data Categories"
                value={(request.expected_data_categories || []).join(', ')}
              />
            </div>

            <div className="mt-4">
              <InfoRow label="Business Reason" value={request.business_reason} />
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Review Action">
            {canReview ? (
              <div className="space-y-4">
                <button
                  onClick={approve}
                  disabled={actionLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Approve and Provision
                </button>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="4"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder="Explain why this request is being rejected..."
                  />
                </div>

                <button
                  onClick={reject}
                  disabled={actionLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Reject Request
                </button>
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                This request has already been reviewed.
              </div>
            )}
          </Card>

          {request.rejection_reason && (
            <Card title="Rejection Reason">
              <p className="text-sm text-slate-700">{request.rejection_reason}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-5 text-lg font-semibold text-slate-950">{title}</h2>
      {children}
    </div>
  );
}